<template>
  <NuxtLink :to="localePath(`/${props.slug}`)" class="blog-card">
    <div class="image-wrapper">
      <NuxtImg
        v-if="props.image"
        format="avif"
        fetchpriority="high"
        :src="cdnUrl + props.image"
        :alt="props.title"
        class="featured-image"
        loading="lazy"
        @error="onImgError"
      />
    </div>
    <div class="content">
      <div class="categories">
        <span
          v-for="category in props.categories"
          :key="category"
          class="category-tag"
        >
          {{ category.toUpperCase() }}
        </span>
      </div>
      <div class="metadata">
        <span class="date">{{ formattedDate }}</span>
        <span class="separator">•</span>
        <span class="read-time">{{ props.readTime }}</span>
      </div>
      <h3 class="title">{{ props.title }}</h3>
      <div class="read-more">
        <span>Read More</span>
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M6 12L10 8L6 4"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </div>
    </div>
  </NuxtLink>
</template>

<script setup>
const config = useRuntimeConfig();
const cdnUrl = config.public.cdnUrl;
const localePath = useLocalePath();
const props = defineProps({
  slug: {
    type: String,
    required: true,
  },
  title: {
    type: String,
    required: true,
  },
  excerpt: {
    type: String,
    required: true,
  },
  image: {
    type: String,
    required: true,
  },
  author: {
    type: String,
    default: "Travvir team",
  },
  date: {
    type: String,
    required: true,
  },
  categories: {
    type: Array,
    default: () => [],
  },
  readTime: {
    type: String,
    default: "5 min read",
  },
});

const formattedDate = computed(() => {
  const date = new Date(props.date);
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
});

const onImgError = (e) => {
  if (e?.target) {
    e.target.src = `${cdnUrl}/static/default/property_preview.jpg`;
  }
};
</script>

<style lang="scss" scoped>
.blog-card {
  min-width: 300px;
  width: 100%;
  font-family: $theme-font;
  display: flex;
  flex-direction: column;
  background: rgba(255, 255, 255, 0.03);
  border-radius: $radius-md;
  overflow: hidden;
  transition: all 0.3s ease;
  border: 1px solid rgba(255, 255, 255, 0.08);
  text-decoration: none;
  // height: 500px;

  &:hover {
    border-color: rgba(255, 255, 255, 0.15);
    transform: translateY(-4px);

    .featured-image {
      transform: scale(1.1);
      height: 400px;
    }

    .read-more {
      color: #5f87ed;
    }
  }

  .image-wrapper {
    width: 100%;
    height: 250px;
    overflow: hidden;
    background: #1a1a1a;

    @include respond-below(sm) {
      height: 200px;
    }

    .featured-image {
      width: 100%;
      height: 100%;
      object-fit: cover;
      transition: transform 0.5s ease;
      margin: unset;
    }
  }

  .content {
    padding: 1.5rem;
    display: flex;
    flex-direction: column;
    flex: 1;

    @include respond-below(sm) {
      padding: 1.25rem;
    }

    .categories {
      display: flex;
      gap: 0.5rem;
      flex-wrap: wrap;
      margin-bottom: 0.75rem;

      .category-tag {
        font-size: 0.65rem;
        font-weight: $fw-semibold;
        letter-spacing: 0.05em;
        color: rgba(255, 255, 255, 0.5);
        padding: 0.3rem 0.75rem;
        background: rgba(255, 255, 255, 0.05);
        border: 1px solid rgba(255, 255, 255, 0.1);
        border-radius: $radius-sm;
      }
    }

    .metadata {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      margin-bottom: 0.875rem;
      font-size: $text-xxs;
      color: rgba(255, 255, 255, 0.4);

      .date,
      .read-time {
        font-weight: $fw-normal;
      }

      .separator {
        color: rgba(255, 255, 255, 0.3);
      }
    }

    .title {
      font-family: $theme-font;
      font-size: $text-md;
      font-weight: $fw-semibold;
      line-height: 1.4;
      color: rgba(255, 255, 255, 0.95);
      margin-bottom: auto;
      display: -webkit-box;
      margin-top: unset;
      -webkit-line-clamp: 3;
      line-clamp: 3;
      -webkit-box-orient: vertical;
      overflow: hidden;

      @include respond-below(sm) {
        font-size: $text-sm;
      }
    }

    .read-more {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      font-size: $text-xs;
      color: rgba(255, 255, 255, 0.6);
      font-weight: $fw-medium;
      transition: color 0.3s ease;
      margin-top: 1rem;

      svg {
        width: 1rem;
        height: 1rem;
        transition: transform 0.3s ease;
      }

      &:hover {
        svg {
          transform: translateX(4px);
        }
      }
    }
  }
}
</style>
